import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Check, Info, Pause, Play, RotateCcw, TimerReset } from 'lucide-react';
import {
  GAIT_SPEED_REFERENCE_NOTE,
  GAIT_SPEED_STANDARD_PROTOCOL,
  GAIT_SPEED_WALKING_AID_OPTIONS,
  buildGaitSpeedPayload,
  calculateGaitSpeedFromTime,
  calculateGaitSpeedReferenceComparison,
  formatGaitSpeed,
  formatGaitSpeedSeconds,
  getGaitSpeedInterpretationText,
  getGaitSpeedReferenceComparisonNote,
  getGaitSpeedSeverityLabel,
  getGaitSpeedSeverityRangeLabel,
  getGaitSpeedSeverityTone,
  getGaitSpeedWalkingAidLabel,
  normalizeGaitSpeedPayload,
  normalizeGaitSpeedTime,
  summarizeGaitSpeedResult,
  type GaitSpeedAttemptPayload,
  type GaitSpeedResultPayload,
  type GaitSpeedWalkingAid,
} from '../gait-speed/gaitSpeedDomain';
import AssessmentWeightAdjustmentField from '../components/AssessmentWeightAdjustmentField';
import { normalizePatientHeightCm, normalizePatientWeightKg, toFiniteNumber } from '../utils/patientMetrics';

type GaitSpeedPatientContext = {
  birthDate?: string | null;
  ageYears?: number | null;
  sex?: string | null;
  weightKg?: number | null;
  heightCm?: number | null;
  identifier?: string | null;
};

interface GaitSpeedTestProps {
  initial?: GaitSpeedResultPayload | null;
  patientContext?: GaitSpeedPatientContext;
  onCancel?: () => void;
  onNext: (result: GaitSpeedResultPayload) => void;
}

type GaitSpeedFormState = {
  completed: boolean;
  attempt1Time: string;
  attempt2Time: string;
  walkingAid: GaitSpeedWalkingAid;
  customAidDescription: string;
  physicalAssistance: boolean;
  oxygenUsed: boolean;
  reasonNotCompleted: string;
  observations: string;
};

type TimerTarget = 'attempt1' | 'attempt2';

export type { GaitSpeedResultPayload };

const formatStopwatch = (elapsedMs: number): string => {
  const totalTenths = Math.floor(elapsedMs / 100);
  const minutes = Math.floor(totalTenths / 600);
  const seconds = Math.floor((totalTenths % 600) / 10);
  const tenths = totalTenths % 10;

  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${tenths}`;
};

const formatElapsedSecondsInput = (elapsedMs: number): string =>
  Number((elapsedMs / 1000).toFixed(2))
    .toString()
    .replace('.', ',');

const buildFormState = (initial?: GaitSpeedResultPayload | null): GaitSpeedFormState => {
  const normalized = normalizeGaitSpeedPayload(initial);
  const attempts = normalized?.attempts ?? [];
  const attempt1 = attempts[0] ?? null;
  const attempt2 = attempts[1] ?? null;

  return {
    completed: normalized?.completed ?? true,
    attempt1Time: typeof attempt1?.timeSeconds === 'number' ? String(attempt1.timeSeconds).replace('.', ',') : '',
    attempt2Time: typeof attempt2?.timeSeconds === 'number' ? String(attempt2.timeSeconds).replace('.', ',') : '',
    walkingAid: normalized?.walkingAid ?? 'none',
    customAidDescription: normalized?.customAidDescription ?? '',
    physicalAssistance: normalized?.physicalAssistance ?? false,
    oxygenUsed: normalized?.oxygenUsed ?? false,
    reasonNotCompleted: normalized?.reasonNotCompleted ?? '',
    observations: normalized?.observations ?? '',
  };
};

const AttemptPreview = ({ label, timeSeconds }: { label: string; timeSeconds: number | null }) => {
  const speedMs = calculateGaitSpeedFromTime(timeSeconds);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
      <div className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">{label}</div>
      <div className="mt-1 text-2xl font-black text-slate-900">
        {speedMs != null ? `${formatGaitSpeed(speedMs)} m/s` : '—'}
      </div>
      <div className="text-sm text-slate-500">{timeSeconds != null ? `${formatGaitSpeedSeconds(timeSeconds)} s` : 'Sem tempo'}</div>
    </div>
  );
};

export default function GaitSpeedTest({ initial = null, patientContext, onCancel, onNext }: GaitSpeedTestProps) {
  const normalizedInitial = normalizeGaitSpeedPayload(initial);
  const initialWeightKg = normalizePatientWeightKg(normalizedInitial?.patientContext?.weightKg ?? patientContext?.weightKg);
  const [form, setForm] = useState<GaitSpeedFormState>(() => buildFormState(initial));
  const [weightKg, setWeightKg] = useState<number | null>(initialWeightKg);
  const [inlineError, setInlineError] = useState<string | null>(null);

  const [timerRunning, setTimerRunning] = useState(false);
  const [timerTarget, setTimerTarget] = useState<TimerTarget>('attempt1');
  const [elapsedMs, setElapsedMs] = useState(0);
  const timerStartedAtRef = useRef<number | null>(null);
  const timerAccumulatedRef = useRef(0);
  const timerIntervalRef = useRef<number | null>(null);

  const attempt1Time = useMemo(() => normalizeGaitSpeedTime(form.attempt1Time), [form.attempt1Time]);
  const attempt2Time = useMemo(() => normalizeGaitSpeedTime(form.attempt2Time), [form.attempt2Time]);
  const attempt2Provided = form.attempt2Time.trim().length > 0;
  const attempt1Speed = calculateGaitSpeedFromTime(attempt1Time);
  const attempt2Speed = calculateGaitSpeedFromTime(attempt2Time);
  const bestSpeedMs =
    attempt1Speed != null && attempt2Speed != null ? Math.max(attempt1Speed, attempt2Speed) : attempt1Speed ?? attempt2Speed;
  const bestTimeSeconds =
    attempt1Speed != null && attempt2Speed != null
      ? attempt1Speed >= attempt2Speed
        ? attempt1Time
        : attempt2Time
      : attempt1Time ?? attempt2Time;
  const walkingAidLabel = useMemo(
    () => getGaitSpeedWalkingAidLabel(form.walkingAid, form.customAidDescription),
    [form.customAidDescription, form.walkingAid],
  );
  const effectivePatientContext = useMemo(
    () => ({
      ...patientContext,
      ageYears: toFiniteNumber(patientContext?.ageYears),
      weightKg,
      heightCm: normalizePatientHeightCm(patientContext?.heightCm),
    }),
    [patientContext, weightKg],
  );
  const referenceComparison = useMemo(
    () => calculateGaitSpeedReferenceComparison(bestSpeedMs, effectivePatientContext),
    [bestSpeedMs, effectivePatientContext],
  );
  const previewLabel = getGaitSpeedSeverityLabel(bestSpeedMs, form.completed, form.physicalAssistance, referenceComparison);
  const previewRange = getGaitSpeedSeverityRangeLabel(bestSpeedMs, form.completed, form.physicalAssistance, referenceComparison);
  const previewTone = getGaitSpeedSeverityTone(bestSpeedMs, form.completed, form.physicalAssistance, referenceComparison);
  const previewInterpretation = getGaitSpeedInterpretationText({
    completed: form.completed,
    timeSeconds: bestTimeSeconds,
    speedMs: bestSpeedMs,
    walkingAidLabel,
    physicalAssistance: form.physicalAssistance,
    oxygenUsed: form.oxygenUsed,
    reasonNotCompleted: form.reasonNotCompleted,
    attemptCount: attempt2Provided ? 2 : 1,
    referenceComparison,
  });

  const formValid =
    (form.completed &&
      attempt1Time != null &&
      (!attempt2Provided || attempt2Time != null) &&
      (form.walkingAid !== 'other' || form.customAidDescription.trim().length > 0)) ||
    (!form.completed &&
      form.reasonNotCompleted.trim().length > 0 &&
      (form.walkingAid !== 'other' || form.customAidDescription.trim().length > 0));

  useEffect(() => {
    if (!timerRunning) {
      if (timerIntervalRef.current != null) {
        window.clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      return;
    }

    timerIntervalRef.current = window.setInterval(() => {
      const startedAt = timerStartedAtRef.current;
      if (startedAt == null) return;
      setElapsedMs(timerAccumulatedRef.current + (Date.now() - startedAt));
    }, 100);

    return () => {
      if (timerIntervalRef.current != null) {
        window.clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [timerRunning]);

  useEffect(() => {
    if (form.completed) return;
    setTimerRunning(false);
  }, [form.completed]);

  const resetTimerState = (nextElapsedMs = 0) => {
    timerStartedAtRef.current = null;
    timerAccumulatedRef.current = nextElapsedMs;
    setElapsedMs(nextElapsedMs);
    setTimerRunning(false);
  };

  const syncTimerToField = (nextElapsedMs: number, target = timerTarget) => {
    if (nextElapsedMs <= 0) return;
    const value = formatElapsedSecondsInput(nextElapsedMs);
    setForm((current) => ({ ...current, [target === 'attempt1' ? 'attempt1Time' : 'attempt2Time']: value }));
  };

  const handleStartTimer = (target: TimerTarget) => {
    if (!form.completed || timerRunning) return;
    setTimerTarget(target);
    timerStartedAtRef.current = Date.now();
    setTimerRunning(true);
  };

  const handlePauseTimer = () => {
    if (!timerRunning) return;

    const startedAt = timerStartedAtRef.current;
    const nextElapsedMs = startedAt == null ? timerAccumulatedRef.current : timerAccumulatedRef.current + (Date.now() - startedAt);
    timerAccumulatedRef.current = nextElapsedMs;
    timerStartedAtRef.current = null;
    setElapsedMs(nextElapsedMs);
    setTimerRunning(false);
    syncTimerToField(nextElapsedMs);
  };

  const handleUseTimerValue = (target: TimerTarget) => {
    syncTimerToField(elapsedMs, target);
  };

  const buildCompletedAttempt = (timeSeconds: number): GaitSpeedAttemptPayload => ({
    completed: true,
    timeSeconds,
    speedMs: calculateGaitSpeedFromTime(timeSeconds),
    walkingAid: form.walkingAid,
    walkingAidLabel,
    customAidDescription: form.walkingAid === 'other' ? form.customAidDescription.trim() : undefined,
    physicalAssistance: form.physicalAssistance,
    oxygenUsed: form.oxygenUsed,
    observations: undefined,
    finishedAt: new Date().toISOString(),
  });

  const handleSubmit = () => {
    setInlineError(null);
    if (!formValid) {
      setInlineError('Preencha os campos obrigatórios antes de salvar o 4MGS.');
      return;
    }

    const attempts = form.completed
      ? [
          ...(attempt1Time != null ? [buildCompletedAttempt(attempt1Time)] : []),
          ...(attempt2Provided && attempt2Time != null ? [buildCompletedAttempt(attempt2Time)] : []),
        ]
      : [
          {
            completed: false,
            timeSeconds: null,
            speedMs: null,
            walkingAid: form.walkingAid,
            walkingAidLabel,
            customAidDescription: form.walkingAid === 'other' ? form.customAidDescription.trim() : undefined,
            physicalAssistance: form.physicalAssistance,
            oxygenUsed: form.oxygenUsed,
            reasonNotCompleted: form.reasonNotCompleted.trim(),
            observations: form.observations.trim() || undefined,
            finishedAt: new Date().toISOString(),
          } satisfies GaitSpeedAttemptPayload,
        ];

    const payload = buildGaitSpeedPayload(attempts, {
      protocol: GAIT_SPEED_STANDARD_PROTOCOL,
      patientContext: effectivePatientContext,
      observations: form.observations.trim() || undefined,
    });
    const normalized = normalizeGaitSpeedPayload(payload);
    onNext(normalized ?? payload);
  };

  const previewSummary = summarizeGaitSpeedResult(
    buildGaitSpeedPayload(
      form.completed && attempt1Time != null
        ? [
            buildCompletedAttempt(attempt1Time),
            ...(attempt2Provided && attempt2Time != null ? [buildCompletedAttempt(attempt2Time)] : []),
          ]
        : [],
      {
        patientContext: effectivePatientContext,
        observations: form.observations.trim() || undefined,
      },
    ),
  );

  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-emerald-950">
        <div className="flex items-start gap-3">
          <TimerReset className="mt-0.5 h-5 w-5 text-emerald-700" />
          <div className="space-y-2">
            <div>
              <p className="font-semibold">4-meter gait speed (4MGS)</p>
              <p className="text-sm text-emerald-900/80">
                Registrar o tempo para caminhar 4 metros em ritmo habitual e calcular a velocidade em m/s.
              </p>
            </div>
            <div className="grid gap-2 text-sm md:grid-cols-2">
              <div>Paciente posicionado na linha de partida com os dois pés.</div>
              <div>Iniciar o cronômetro quando o paciente começa a se mover.</div>
              <div>Parar quando o primeiro pé ultrapassar completamente o cone dos 4 metros.</div>
              <div>Registrar uma ou duas tentativas e usar a maior velocidade.</div>
            </div>
          </div>
        </div>
      </div>

      <AssessmentWeightAdjustmentField
        value={weightKg}
        baselineValue={initialWeightKg}
        onChange={setWeightKg}
        helperText="Use quando o peso aferido nesta avaliação estiver diferente do cadastro. O ajuste é local e acompanha apenas este 4MGS."
      />

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-700">Status do teste</label>
          <div className="grid gap-2 sm:grid-cols-2">
            <label
              className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 ${
                form.completed ? 'border-emerald-300 bg-emerald-50 text-emerald-900' : 'border-slate-200 bg-white text-slate-700'
              }`}
            >
              <input
                type="radio"
                name="gait_speed_status"
                checked={form.completed}
                onChange={() => setForm((current) => ({ ...current, completed: true }))}
              />
              <span className="font-medium">Concluído</span>
            </label>
            <label
              className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 ${
                !form.completed ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-slate-200 bg-white text-slate-700'
              }`}
            >
              <input
                type="radio"
                name="gait_speed_status"
                checked={!form.completed}
                onChange={() => setForm((current) => ({ ...current, completed: false }))}
              />
              <span className="font-medium">Não concluiu</span>
            </label>
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-700">Auxílio de marcha utilizado</label>
          <select
            value={form.walkingAid}
            onChange={(event) => setForm((current) => ({ ...current, walkingAid: event.target.value as GaitSpeedWalkingAid }))}
            className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
          >
            {GAIT_SPEED_WALKING_AID_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {form.walkingAid === 'other' && (
            <input
              value={form.customAidDescription}
              onChange={(event) => setForm((current) => ({ ...current, customAidDescription: event.target.value }))}
              className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
              placeholder="Descrever o dispositivo utilizado"
            />
          )}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-700">1ª tentativa (segundos)</label>
          <input
            value={form.attempt1Time}
            onChange={(event) => setForm((current) => ({ ...current, attempt1Time: event.target.value }))}
            disabled={!form.completed}
            inputMode="decimal"
            placeholder="Ex.: 4,80"
            className="w-full rounded-xl border border-slate-300 px-3 py-2.5 disabled:cursor-not-allowed disabled:bg-slate-100 focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-700">2ª tentativa (opcional)</label>
          <input
            value={form.attempt2Time}
            onChange={(event) => setForm((current) => ({ ...current, attempt2Time: event.target.value }))}
            disabled={!form.completed}
            inputMode="decimal"
            placeholder="Ex.: 4,55"
            className="w-full rounded-xl border border-slate-300 px-3 py-2.5 disabled:cursor-not-allowed disabled:bg-slate-100 focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>

      {!form.completed && (
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-700">Motivo se não concluiu</label>
          <input
            value={form.reasonNotCompleted}
            onChange={(event) => setForm((current) => ({ ...current, reasonNotCompleted: event.target.value }))}
            placeholder="Ex.: não conseguiu caminhar sem ajuda"
            className="w-full rounded-xl border border-slate-300 px-3 py-2.5 focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
          />
        </div>
      )}

      <div className="rounded-[24px] border border-emerald-100 bg-emerald-50/70 p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="text-[10px] font-black uppercase tracking-[0.22em] text-emerald-700">Cronômetro de apoio</div>
            <div className="mt-2 text-4xl font-black tracking-tight text-emerald-950">{formatStopwatch(elapsedMs)}</div>
            <p className="mt-2 text-sm text-emerald-900/80">
              Use como apoio no runner e transfira o tempo para a tentativa desejada ao final da medida.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {timerRunning ? (
              <button
                type="button"
                onClick={handlePauseTimer}
                className="inline-flex items-center rounded-xl bg-amber-500 px-3 py-2 text-sm font-medium text-white hover:bg-amber-600"
              >
                <Pause className="mr-2 h-4 w-4" />
                Pausar
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => handleStartTimer('attempt1')}
                  disabled={!form.completed}
                  className="inline-flex items-center rounded-xl bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Play className="mr-2 h-4 w-4" />
                  Iniciar 1ª
                </button>
                <button
                  type="button"
                  onClick={() => handleStartTimer('attempt2')}
                  disabled={!form.completed}
                  className="inline-flex items-center rounded-xl border border-emerald-200 bg-white px-3 py-2 text-sm font-medium text-emerald-800 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Play className="mr-2 h-4 w-4" />
                  Iniciar 2ª
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => handleUseTimerValue(timerTarget)}
              disabled={!form.completed || elapsedMs <= 0}
              className="inline-flex items-center rounded-xl border border-emerald-200 bg-white px-3 py-2 text-sm font-medium text-emerald-800 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Check className="mr-2 h-4 w-4" />
              Usar em {timerTarget === 'attempt1' ? '1ª' : '2ª'}
            </button>
            <button
              type="button"
              onClick={() => resetTimerState(0)}
              disabled={elapsedMs <= 0 && !timerRunning}
              className="inline-flex items-center rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <RotateCcw className="mr-2 h-4 w-4" />
              Zerar
            </button>
          </div>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <label className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3">
          <input
            type="checkbox"
            checked={form.oxygenUsed}
            onChange={(event) => setForm((current) => ({ ...current, oxygenUsed: event.target.checked }))}
            className="mt-1"
          />
          <div>
            <div className="font-medium text-slate-900">Usou oxigênio suplementar</div>
            <div className="text-sm text-slate-600">Registrar quando o paciente realizou o teste com oxigênio.</div>
          </div>
        </label>

        <label className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3">
          <input
            type="checkbox"
            checked={form.physicalAssistance}
            onChange={(event) => setForm((current) => ({ ...current, physicalAssistance: event.target.checked }))}
            className="mt-1"
          />
          <div>
            <div className="font-medium text-slate-900">Necessitou assistência física</div>
            <div className="text-sm text-slate-600">Se marcado, o resultado fica menos comparável aos pontos de corte.</div>
          </div>
        </label>
      </div>

      {form.physicalAssistance && (
        <div className="flex items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4" />
          <p>A assistência física altera a comparabilidade com os valores de referência e pontos de corte do 4MGS.</p>
        </div>
      )}

      <div className="space-y-2">
        <label className="text-sm font-medium text-slate-700">Observações clínicas</label>
        <textarea
          rows={3}
          value={form.observations}
          onChange={(event) => setForm((current) => ({ ...current, observations: event.target.value }))}
          className="w-full rounded-2xl border border-slate-300 px-3 py-2.5 focus:border-primary-500 focus:ring-2 focus:ring-primary-500"
          placeholder="Ex.: marcha hesitante, base alargada, pausa antes do cone..."
        />
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <AttemptPreview label="1ª tentativa" timeSeconds={attempt1Time} />
        <AttemptPreview label="2ª tentativa" timeSeconds={attempt2Time} />
      </div>

      <div className="rounded-[24px] border border-slate-100 bg-slate-50 p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">Prévia automática</div>
            <h4 className="mt-2 text-2xl font-black tracking-tight text-slate-900">
              {previewSummary.completed && previewSummary.bestSpeedMs != null ? `${formatGaitSpeed(previewSummary.bestSpeedMs)} m/s` : 'Sem velocidade válida'}
            </h4>
          </div>
          <div className={`rounded-full px-3 py-1.5 text-xs font-bold shadow-sm ${previewTone}`}>
            {previewLabel} • {previewRange}
          </div>
        </div>
        <p className="mt-4 text-sm leading-relaxed text-slate-600">{previewInterpretation}</p>
        <div className="mt-4 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
          <div className="font-semibold text-slate-900">Referência prevista</div>
          <div className="mt-1">{referenceComparison ? getGaitSpeedReferenceComparisonNote(referenceComparison) : getGaitSpeedReferenceComparisonNote(null)}</div>
        </div>
      </div>

      <div className="flex items-start gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
        <Info className="mt-0.5 h-4 w-4 text-slate-500" />
        <p>{GAIT_SPEED_REFERENCE_NOTE}</p>
      </div>

      {inlineError ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{inlineError}</div>
      ) : null}

      <div className="flex items-center justify-between pt-1">
        <button type="button" className="text-sm text-gray-600 hover:text-gray-800" onClick={onCancel}>
          Cancelar
        </button>

        <button
          type="button"
          className="inline-flex items-center rounded-md bg-green-600 px-3 py-2 text-white disabled:opacity-50"
          disabled={!formValid}
          onClick={handleSubmit}
        >
          Salvar 4MGS
          <Check className="ml-1 h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
